import { Injectable, inject } from '@angular/core';

import { Observable, catchError, filter, map, of, switchMap, tap } from 'rxjs';

import {
  CamBaseService,
  CamTenantService,
  GraphEndpoint,
  HandleComplexRequest,
  HandleSimpleRequest,
} from '@camelot/server';
import { APPLICATION_CONFIG, IApplicationConfig, isNonNullable } from '@camelot/utils';

import { CurrentUser } from './dto/current-user';
import { functionProps } from './dto/function';
import { ModifyUserPayloadInput } from './dto/modify-user-payload-input';
import { User, userProps } from './dto/user';
import { UserAddedPayload } from './dto/user-added-payload';
import { ADD_USER, UPDATE_CURRENT_USER, UPDATE_USER } from './user-mutations';
import {
  GET_CURRENT_USER,
  GET_MY_CONTACTS,
  GET_SEARCH_USERS_CUSTOMERS,
  GET_USERS,
  GET_USER_BY_ID,
} from './user-queries';

export const cachedQueryName = {
  currentUser: 'currentUser',
  userById: 'userById',
};
const graphEndpoint: GraphEndpoint = {
  clientName: 'userService',
  endpoint: 'user',
};

@Injectable({
  providedIn: 'root',
})
export class CamUsersService extends CamBaseService {
  private readonly _applicationConfig: IApplicationConfig = inject(APPLICATION_CONFIG);
  private readonly _tenantService = inject(CamTenantService);

  public users = new HandleSimpleRequest<User[]>();
  public usersCustomers = new HandleSimpleRequest<User[]>();

  public userById = new HandleComplexRequest<User>();
  public user = new HandleComplexRequest<User>();
  public currentUser = new HandleSimpleRequest<User>();
  public currentUserContactIds = new HandleSimpleRequest<string[]>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });

    this._registerSubscription(
      this.currentUser
        .get$()
        .pipe(filter(isNonNullable))
        .subscribe(user => this._tenantService.setTenantInformations(user.tenantInformations))
    );
  }

  public fetchUsers$() {
    return this.users.fetch(
      this._graphService
        .fetchPagedQueryList<User>(
          GET_USERS(
            '',
            `
              ${userProps.get('id')}
              ${userProps.get('userName')}
              ${userProps.get('firstName')}
              ${userProps.get('lastName')}
              ${userProps.get('phoneNumber')}
              ${userProps.get('isDisabled')}
              ${userProps.get('functions')} {
                ${functionProps.get('id')}
                ${functionProps.get('name')}
              }
              ${userProps.get('culture')}
              ${userProps.get('trigram')}
              ${userProps.get('picture')}
              ${userProps.get('pictureDocumentId')}

            `,
            `order: { lastName: ASC }`
          ),
          'users',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchUsersByIds$(ids: string[]) {
    return this.users.fetch(
      this._graphService
        .fetchPagedQueryList<User>(
          GET_USERS(
            `where: { id: { in: [${ids.map(id => `"${id}"`).join(', ')}] } }`,
            `
              ${userProps.get('id')}
              ${userProps.get('userName')}
              ${userProps.get('firstName')}
              ${userProps.get('lastName')}
              ${userProps.get('picture')}
              ${userProps.get('pictureDocumentId')}
              ${userProps.get('trigram')}
            `,
            `order: { lastName: ASC }`
          ),
          'users',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchUser$(id: string) {
    return this.user.fetch(
      id,
      this._graphService.fetchQuery<User>(GET_USER_BY_ID(id), 'userById', graphEndpoint.clientName)
    );
  }

  public fetchCurrentUser$() {
    return this.currentUser.fetch(
      this._graphService.fetchQueryBuilder<User>(
        GET_CURRENT_USER(!this._applicationConfig.isCustomerApplication),
        graphEndpoint.clientName
      )
    );
  }

  public currentUserId$(): Observable<string> {
    return this.currentUser.get$().pipe(
      filter(isNonNullable),
      map(user => user.id)
    );
  }

  public fetchCurrentUserContactIds$() {
    return this.currentUserContactIds
      .fetch(
        this._graphService
          .fetchQueryList<string>(GET_MY_CONTACTS(), 'myContacts', graphEndpoint.clientName)
          .pipe(map(data => data ?? []))
      )
      .pipe(
        tap(() => {
          this._graphService.contactsLoaded$.next(true);
        })
      );
  }

  public fetchUsersCustomers$(search?: string | null) {
    if (!search || search.length < 3) {
      return of([]);
    }
    return this._graphService
      .fetchPagedQueryList<User>(
        GET_SEARCH_USERS_CUSTOMERS(
          search,
          `
              ${userProps.get('id')}
              ${userProps.get('picture')}
              ${userProps.get('pictureDocumentId')}
              ${userProps.get('trigram')}
              ${userProps.get('firstName')}
              ${userProps.get('lastName')}
              ${userProps.get('userName')}
            `
        ),
        'searchUsersCustomers',
        graphEndpoint.clientName
      )
      .pipe(
        filter(isNonNullable),
        map(data => data.items ?? []),
        tap(list =>
          list.forEach(element => {
            this.userById.add(element.id, element);
          })
        )
      );
  }

  public addUser$(userAddedPayload: UserAddedPayload): Observable<User[] | null> {
    return this._graphService.mutateBuilder<User>(ADD_USER(userAddedPayload), graphEndpoint.clientName).pipe(
      filter(isNonNullable),
      switchMap(() => this.fetchUsers$()),
      catchError(error => {
        console.error('Error adding user:', error);
        return of(null);
      })
    );
  }

  public updateCurrentUser$(user: ModifyUserPayloadInput) {
    return this._graphService
      .mutateBuilder<CurrentUser>(UPDATE_CURRENT_USER(user), graphEndpoint.clientName, [cachedQueryName.currentUser])
      .pipe(
        switchMap(() => this.fetchCurrentUser$()),
        catchError(error => {
          console.error('Error updating user:', error);
          return of(null);
        })
      );
  }

  public updateUser$(payload: ModifyUserPayloadInput) {
    return this._graphService
      .mutateBuilder<User>(UPDATE_USER(payload), graphEndpoint.clientName, [cachedQueryName.userById])
      .pipe(switchMap(() => this.fetchUser$(payload.id!)));
  }
}
